import React, { useMemo, useState, useRef, useEffect } from "react";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import DDMultipleWithChips, {
  Option as ChipOption,
} from "../../components/Atomos/DropDown/DDMultipleWithChips";
import { TrashCan } from "../../components/Atomos/Icons/TrashCan";
import {
  RecipientCondition,
  Operator,
  ConditionOperator,
  ConditionFieldConfig,
  ConditionValueOption,
} from "./types";

const DEFAULT_OPERATOR_OPTIONS: ConditionOperator[] = ["is-one-of", "is-not"];

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  is: "is",
  "is-one-of": "is one of",
  "is-not": "is not",
  like: "like",
};

const TrashIcon: React.FC = () => (
  <div className="w-5 h-5 fill-current">
    <TrashCan />
  </div>
);

const normalizeTextChipValue = (value: string, fieldId?: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "";
  }
  if (fieldId === "mail-address") {
    return trimmedValue.toLowerCase();
  }
  return trimmedValue;
};

const OperatorDropdown: React.FC<{
  value: ConditionOperator;
  options: ConditionOperator[];
  disabled?: boolean;
  onChange: (value: ConditionOperator) => void;
}> = ({ value, options, disabled = false, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const canOpen = !disabled && options.length > 1;
  const selectedLabel =
    OPERATOR_LABELS[value] || OPERATOR_LABELS["is-one-of"];

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => canOpen && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-1 text-sm font-medium transition-colors ${
          disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-font hover:text-Default"
        }`}
      >
        {selectedLabel}
        {options.length > 1 && (
          <svg
            className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </button>
      {isOpen && canOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[100px]">
          {options.map((operatorOption) => (
            <button
              key={operatorOption}
              type="button"
              onClick={() => {
                onChange(operatorOption);
                setIsOpen(false);
              }}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                value === operatorOption
                  ? "bg-gray-50 font-medium text-Default"
                  : "text-gray-font"
              }`}
            >
              {OPERATOR_LABELS[operatorOption]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export interface ConditionRowProps {
  condition: RecipientCondition;
  operator: Operator;
  fieldOptions: ConditionFieldConfig[];
  fieldConfig?: ConditionFieldConfig;
  valueOptions: ConditionValueOption[];
  isFieldOptionsLoading?: boolean;
  isValueOptionsLoading?: boolean;
  mailAddressValidationMessage?: string;
  isMailAddressValidationLoading?: boolean;
  readOnly?: boolean;
  canRemove?: boolean;
  onFieldChange: (id: string, fieldId: string) => void;
  onConditionOperatorChange: (id: string, conditionOperator: ConditionOperator) => void;
  onValueChange: (id: string, valueId: string) => void;
  onMailAddressBlur?: (id: string, value: string) => void;
  onRemove: (id: string) => void;
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  operator,
  fieldOptions,
  fieldConfig,
  valueOptions,
  isFieldOptionsLoading = false,
  isValueOptionsLoading = false,
  mailAddressValidationMessage = "",
  isMailAddressValidationLoading = false,
  readOnly = false,
  canRemove = true,
  onFieldChange,
  onConditionOperatorChange,
  onValueChange,
  onMailAddressBlur,
  onRemove,
}) => {
  const selectedFieldName = useMemo(() => {
    return (
      fieldOptions.find((fieldOption) => fieldOption.id === condition.fieldId)?.name ||
      ""
    );
  }, [condition.fieldId, fieldOptions]);
  const selectedFieldOption = useMemo(
    () =>
      fieldOptions.find((fieldOption) => fieldOption.id === condition.fieldId) ||
      null,
    [condition.fieldId, fieldOptions],
  );

  const operatorOptions = useMemo<ConditionOperator[]>(() => {
    const options =
      fieldConfig && fieldConfig.operators.length > 0
        ? fieldConfig.operators
        : DEFAULT_OPERATOR_OPTIONS;
    return options;
  }, [fieldConfig]);

  const selectedMultiValues = useMemo(() => {
    if (!condition.valueId) {
      return [];
    }
    return condition.valueId
      .split("|")
      .map((value) => value.trim())
      .filter(Boolean);
  }, [condition.valueId]);

  const isSelectInput = fieldConfig?.inputType === "select";
  const isTextInput = fieldConfig?.inputType === "text";
  const isMultiValueOperator =
    condition.conditionOperator === "is" ||
    condition.conditionOperator === "is-one-of";
  const isTextMultiValueOperator =
    condition.conditionOperator === "is" ||
    condition.conditionOperator === "is-one-of" ||
    condition.conditionOperator === "is-not";
  const isMultiSelect =
    isSelectInput &&
    isMultiValueOperator &&
    fieldConfig?.allowMultiValueForIs !== false;
  const isTextMultiValueInput =
    isTextInput &&
    isTextMultiValueOperator &&
    fieldConfig?.allowMultiValueForIs !== false;

  const selectedValueName = useMemo(() => {
    if (!condition.valueId) {
      return "";
    }

    if (isMultiSelect) {
      return selectedMultiValues
        .map((id) => valueOptions.find((option) => option.id === id)?.name || id)
        .join(" | ");
    }

    const firstValue = condition.valueId.split("|")[0] || "";
    const selectedOption = valueOptions.find((option) => option.id === firstValue);
    return selectedOption?.name || firstValue;
  }, [condition.valueId, isMultiSelect, selectedMultiValues, valueOptions]);
  const selectedValueOption = useMemo(() => {
    if (!condition.valueId || isMultiSelect) {
      return null;
    }

    const firstValue = condition.valueId.split("|")[0] || "";
    return valueOptions.find((option) => option.id === firstValue) || null;
  }, [condition.valueId, isMultiSelect, valueOptions]);

  const fieldDropdownData = useMemo(
    () => fieldOptions.map((fieldOption) => ({ id: fieldOption.id, name: fieldOption.name })),
    [fieldOptions],
  );
  const valueDropdownData = useMemo<ChipOption[]>(
    () => valueOptions.map((valueOption) => ({ id: valueOption.id, name: valueOption.name })),
    [valueOptions],
  );
  const selectedMultiOptions = useMemo<ChipOption[]>(() => {
    if (!selectedMultiValues.length) {
      return [];
    }
    const valueOptionsById = new Map(
      valueDropdownData.map((valueOption) => [valueOption.id, valueOption]),
    );
    return selectedMultiValues.map(
      (valueId) => valueOptionsById.get(valueId) || { id: valueId, name: valueId },
    );
  }, [selectedMultiValues, valueDropdownData]);
  const selectedTextChipOptions = useMemo<ChipOption[]>(
    () =>
      selectedMultiValues.map((value) => ({
        id: value,
        name: value,
      })),
    [selectedMultiValues],
  );

  const valuePlaceholder = isValueOptionsLoading
    ? "Loading options..."
    : fieldConfig?.placeholder || "Select value";

  return (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0">
        <span
          className="relative z-10 inline-flex items-center justify-center w-[73px] min-h-[44px] h-[45px] rounded-full text-xs font-medium bg-[#EBEBEB] text-gray-font"
          style={{ padding: "13px 5px" }}
        >
          {operator}
        </span>
      </div>

      <div className="flex-1">
        <DropDownList
          title=""
          data={fieldDropdownData}
          selectedObjst={selectedFieldName}
          selectedValue={
            selectedFieldOption
              ? { id: selectedFieldOption.id, name: selectedFieldOption.name }
              : null
          }
          seter={(value: { id: string; name: string } | null) =>
            onFieldChange(condition.id, value?.id || "")
          }
          error={false}
          placeholder={isFieldOptionsLoading ? "Loading fields..." : "Select field"}
          getid
          compareBy="id"
          disabled={readOnly || isFieldOptionsLoading}
          internalSearch
        />
      </div>

      <div className="flex-shrink-0 px-2">
        <OperatorDropdown
          value={condition.conditionOperator}
          options={operatorOptions}
          disabled={readOnly}
          onChange={(operatorValue) =>
            onConditionOperatorChange(condition.id, operatorValue)
          }
        />
      </div>

      <div className="flex-1">
        {isTextInput ? (
          <div className="flex flex-col gap-1">
            {isTextMultiValueInput ? (
              <DDMultipleWithChips
                title=""
                objects={[]}
                selectedObjst={selectedTextChipOptions}
                seter={(nextSelected) => {
                  const resolvedSelection =
                    typeof nextSelected === "function"
                      ? nextSelected(selectedTextChipOptions)
                      : nextSelected;
                  const normalizedValues = resolvedSelection.reduce<string[]>(
                    (acc, selectedOption) => {
                      const nextValue = normalizeTextChipValue(
                        String(selectedOption.id || selectedOption.name || ""),
                        fieldConfig?.id,
                      );
                      const exists = acc.some(
                        (currentValue) =>
                          currentValue.toLowerCase() === nextValue.toLowerCase(),
                      );
                      if (!nextValue || exists) {
                        return acc;
                      }
                      acc.push(nextValue);
                      return acc;
                    },
                    [],
                  );
                  onValueChange(condition.id, normalizedValues.join("|"));
                }}
                disabled={!condition.fieldId || readOnly}
                placeholder={valuePlaceholder}
                allowCustomOptions
                createCustomOption={(rawValue) => {
                  const normalizedValue = normalizeTextChipValue(
                    rawValue,
                    fieldConfig?.id,
                  );
                  if (!normalizedValue) {
                    return null;
                  }
                  return {
                    id: normalizedValue,
                    name: normalizedValue,
                  };
                }}
                noOptionsMessage="Type a value and press Enter"
                onInputBlur={() => {
                  if (fieldConfig?.id === "mail-address") {
                    onMailAddressBlur?.(condition.id, condition.valueId);
                  }
                }}
              />
            ) : (
              <input
                type="text"
                value={condition.valueId}
                onChange={(event) => onValueChange(condition.id, event.target.value)}
                onBlur={() => {
                  if (fieldConfig?.id === "mail-address") {
                    onMailAddressBlur?.(condition.id, condition.valueId);
                  }
                }}
                placeholder={fieldConfig?.placeholder || "Enter value"}
                className={`w-full px-4 py-2.5 border rounded text-sm focus:outline-none focus:border-Default ${
                  readOnly
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : "border-gray-200"
                }`}
                disabled={readOnly}
              />
            )}
            {fieldConfig?.id === "mail-address" &&
              isMailAddressValidationLoading && (
                <span className="text-xs text-gray-500">Validating email...</span>
              )}
            {fieldConfig?.id === "mail-address" &&
              !isMailAddressValidationLoading &&
              mailAddressValidationMessage && (
                <span className="text-xs text-feedback-error">
                  {mailAddressValidationMessage}
                </span>
              )}
          </div>
        ) : isMultiSelect ? (
          <DDMultipleWithChips
            title=""
            objects={valueDropdownData}
            selectedObjst={selectedMultiOptions}
            seter={(nextSelected) => {
              const resolvedSelection =
                typeof nextSelected === "function"
                  ? nextSelected(selectedMultiOptions)
                  : nextSelected;
              const valueIds = resolvedSelection
                .map((selectedOption) => selectedOption.id || selectedOption.name)
                .filter(Boolean);
              onValueChange(condition.id, valueIds.join("|"));
            }}
            disabled={!condition.fieldId || readOnly || isValueOptionsLoading}
            placeholder={valuePlaceholder}
          />
        ) : (
          <DropDownList
            title=""
            data={valueOptions}
            selectedObjst={selectedValueName}
            selectedValue={
              selectedValueOption
                ? { id: selectedValueOption.id, name: selectedValueOption.name }
                : null
            }
            seter={(value: { id: string; name: string } | null) =>
              onValueChange(condition.id, value?.id || "")
            }
            error={false}
            placeholder={valuePlaceholder}
            getid
            compareBy="id"
            disabled={
              !condition.fieldId ||
              !isSelectInput ||
              readOnly ||
              isValueOptionsLoading
            }
            internalSearch
          />
        )}
      </div>

      <div className="flex-shrink-0 w-6">
        <button
          type="button"
          onClick={() => {
            if (!canRemove) {
              return;
            }
            onRemove(condition.id);
          }}
          className={`transition-colors ${
            canRemove
              ? "text-feedback-error hover:text-primary"
              : "text-gray-300 cursor-not-allowed"
          }`}
          aria-label="Remove condition"
          disabled={!canRemove}
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  );
};

export default React.memo(ConditionRow);
